import { useMemo, useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ShieldOff, ShieldCheck, Search, Star } from 'lucide-react';
import type { ActiveOrder } from '@t212/shared';
import type { PositionWithTv } from '@/lib/api';
import { fmtMoney, fmtNumber, fmtSignedMoney, fmtSignedPercent } from '@/lib/format';
import { RowChartPreview } from './RowChartPreview';
import { usePinnedTickers } from '@/lib/usePinnedTickers';
import { cn } from '@/lib/cn';

type Props = {
  positions: PositionWithTv[];
  totalValue: number;
  activeOrders: ActiveOrder[];
};

export function PositionsTable({ positions, totalValue, activeOrders }: Props) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'currentValue', desc: true },
  ]);
  const [filter, setFilter] = useState('');
  const { isPinned, toggle: togglePin } = usePinnedTickers();

  const stopCovered = useMemo(() => {
    const set = new Set<string>();
    for (const o of activeOrders) {
      if ((o.type === 'STOP' || o.type === 'STOP_LIMIT') && o.side === 'SELL') {
        set.add(o.ticker);
      }
    }
    return set;
  }, [activeOrders]);

  const data = useMemo(
    () =>
      positions.map((p) => {
        const pnl = p.walletImpact.unrealizedProfitLoss;
        const pnlPct = (pnl / Math.max(0.01, p.walletImpact.totalCost)) * 100;
        const pctOfAccount =
          totalValue > 0 ? (p.walletImpact.currentValue / totalValue) * 100 : 0;
        const daysHeld = Math.floor(
          (Date.now() - new Date(p.createdAt).getTime()) / 86_400_000,
        );
        return {
          ticker: p.instrument.ticker,
          name: p.instrument.name,
          symbol: p.instrument.ticker.split('_')[0] ?? p.instrument.ticker,
          quantity: p.quantity,
          avg: p.averagePricePaid,
          price: p.currentPrice,
          currentValue: p.walletImpact.currentValue,
          pnl,
          pnlPct,
          pctOfAccount,
          sector: p.instrument.sector ?? '',
          daysHeld,
          hasStop: stopCovered.has(p.instrument.ticker),
          currency: p.instrument.currency,
        };
      }),
    [positions, totalValue, stopCovered],
  );

  type Row = (typeof data)[number];

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        id: 'pin',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const pinned = isPinned(row.original.ticker);
          return (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                togglePin(row.original.ticker);
              }}
              title={pinned ? 'Unpin' : 'Pin to top'}
              aria-label={pinned ? `Unpin ${row.original.symbol}` : `Pin ${row.original.symbol} to top`}
              aria-pressed={pinned}
              className={cn(
                'p-1.5 -m-1.5 rounded opacity-50 hover:opacity-100 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                pinned && 'opacity-100',
              )}
            >
              <Star
                className={cn(
                  'size-4',
                  pinned ? 'fill-amber-400 text-amber-400' : 'text-zinc-400',
                )}
              />
            </button>
          );
        },
      },
      {
        accessorKey: 'symbol',
        header: 'Symbol',
        cell: ({ row }) => (
          <Link
            to="/positions/$ticker"
            params={{ ticker: row.original.ticker }}
            className="flex items-center gap-2"
          >
            <span className="font-mono text-zinc-200">{row.original.symbol}</span>
            <span className="text-xs text-zinc-400 truncate max-w-[180px]">
              {row.original.name}
            </span>
          </Link>
        ),
      },
      {
        accessorKey: 'quantity',
        header: 'Qty',
        cell: ({ getValue }) => (
          <span className="num text-zinc-300">{fmtNumber(getValue<number>(), 0)}</span>
        ),
      },
      {
        accessorKey: 'avg',
        header: 'Avg',
        cell: ({ getValue }) => (
          <span className="num text-zinc-400">{fmtNumber(getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: ({ getValue }) => (
          <span className="num text-zinc-100">{fmtNumber(getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'currentValue',
        header: 'Value',
        cell: ({ getValue }) => (
          <span className="num text-zinc-100">{fmtMoney(getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'pnl',
        header: 'P&L',
        cell: ({ row }) => {
          const v = row.original.pnl;
          return (
            <span className={cn('num', v >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
              {fmtSignedMoney(v)}
            </span>
          );
        },
      },
      {
        accessorKey: 'pnlPct',
        header: 'P&L %',
        cell: ({ row }) => {
          const v = row.original.pnlPct;
          return (
            <span className={cn('num', v >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
              {fmtSignedPercent(v)}
            </span>
          );
        },
      },
      {
        accessorKey: 'pctOfAccount',
        header: '% acct',
        cell: ({ row }) => {
          const v = row.original.pctOfAccount;
          return (
            <span className={cn('num', v > 10 ? 'text-amber-400' : 'text-zinc-300')}>
              {v.toFixed(1)}%
            </span>
          );
        },
      },
      {
        accessorKey: 'sector',
        header: 'Sector',
        cell: ({ getValue }) => (
          <span className="text-xs text-zinc-400">{getValue<string>() || '-'}</span>
        ),
      },
      {
        accessorKey: 'daysHeld',
        header: 'Held',
        cell: ({ getValue }) => (
          <span className="text-xs text-zinc-400">{getValue<number>()}d</span>
        ),
      },
      {
        accessorKey: 'hasStop',
        header: 'Stop',
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <ShieldCheck className="size-4 text-emerald-400" />
          ) : (
            <ShieldOff className="size-4 text-rose-400" />
          ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: filter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Pinned rows float to the top regardless of column sort. We post-sort
  // the rendered rows here rather than fighting TanStack Table's sort state.
  const rows = useMemo(() => {
    const all = table.getRowModel().rows;
    return [...all].sort((a, b) => {
      const aP = isPinned(a.original.ticker) ? 1 : 0;
      const bP = isPinned(b.original.ticker) ? 1 : 0;
      return bP - aP;
    });
  }, [table, table.getRowModel().rows, isPinned]);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-2 flex-1">
          <Search className="size-4 text-zinc-400" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter positions"
            className="bg-transparent outline-none text-sm w-full placeholder:text-zinc-400"
          />
        </div>
        <span className="text-[11px] text-zinc-400">
          {table.getRowModel().rows.length} of {positions.length}
        </span>
      </div>
      {/* Mobile card layout. Below md, the 13-column table is unusable on a
          390pt viewport — Stop status (the column Andy opens the dashboard for)
          lives in the rightmost off-screen column. Cards put the same priority
          fields one tap away. */}
      <ul className="md:hidden divide-y divide-zinc-900">
        {rows.map((row) => {
          const r = row.original;
          return (
            <li key={row.id} className={cn('p-3', isPinned(r.ticker) && 'bg-amber-500/[0.04]')}>
              <Link
                to="/positions/$ticker"
                params={{ ticker: r.ticker }}
                className="flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-zinc-100 text-sm">{r.symbol}</span>
                    {r.hasStop ? (
                      <ShieldCheck className="size-3.5 text-emerald-400" aria-label="Stop protected" />
                    ) : (
                      <ShieldOff className="size-3.5 text-rose-400" aria-label="No stop" />
                    )}
                    <span
                      className={cn(
                        'num text-[11px] ml-auto',
                        r.pctOfAccount > 10 ? 'text-amber-400' : 'text-zinc-400',
                      )}
                    >
                      {r.pctOfAccount.toFixed(1)}% acct
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-400 truncate mt-0.5">{r.name}</div>
                  <div className="flex items-baseline gap-3 mt-1.5">
                    <span className="num text-zinc-100 text-sm">{fmtMoney(r.currentValue)}</span>
                    <span
                      className={cn(
                        'num text-xs',
                        r.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400',
                      )}
                    >
                      {fmtSignedMoney(r.pnl)} ({fmtSignedPercent(r.pnlPct)})
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
        {rows.length === 0 && (
          <li className="p-6 text-center text-zinc-400 text-sm">No positions match.</li>
        )}
      </ul>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 sticky top-0 z-10">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="text-[11px] uppercase tracking-wider text-zinc-400">
                {hg.headers.map((h) => {
                  const canSort = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  const ariaSort =
                    dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none';
                  const headerContent = (
                    <span className="inline-flex items-center gap-1">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {dir === 'asc' && <ArrowUp className="size-3" />}
                      {dir === 'desc' && <ArrowDown className="size-3" />}
                    </span>
                  );
                  return (
                    <th
                      key={h.id}
                      aria-sort={canSort ? ariaSort : undefined}
                      className="text-left font-normal px-3 py-2 whitespace-nowrap border-b border-zinc-800"
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:rounded"
                        >
                          {headerContent}
                        </button>
                      ) : (
                        headerContent
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map((row) => (
              <RowChartPreview
                key={row.id}
                ticker={row.original.ticker}
                pnlPositive={row.original.pnl >= 0}
              >
                <tr
                  className={cn(
                    'border-b border-zinc-900 hover:bg-zinc-900/40 transition-colors',
                    isPinned(row.original.ticker) && 'bg-amber-500/[0.04]',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2.5 whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              </RowChartPreview>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
